import { api, ApiError, getAccessToken } from "@/services/api";
import type { AgentRun, AgentRunEvent } from "@/types";

export interface AgentRunTransport {
  start(
    briefId: number,
    input: { content: string; assetIds: number[] },
  ): Promise<AgentRun>;
  active(briefId: number): Promise<AgentRun | null>;
  latest(briefId: number): Promise<{ run: AgentRun | null; events: AgentRunEvent[] }>;
  follow(
    runId: number,
    onEvent: (event: AgentRunEvent) => void,
  ): Promise<void>;
  cancel(runId: number): Promise<void>;
}

const terminalEvents = new Set(["complete", "error", "cancelled"]);
const wait = (duration: number) =>
  new Promise((resolve) => window.setTimeout(resolve, duration));

function consumeEvent(
  event: AgentRunEvent,
  onEvent: (event: AgentRunEvent) => void,
) {
  onEvent(event);
  return terminalEvents.has(event.type);
}

async function pollRun(
  runId: number,
  afterId: number,
  onEvent: (event: AgentRunEvent) => void,
) {
  let cursor = afterId;
  while (true) {
    const payload = await api<{ run: AgentRun; events: AgentRunEvent[] }>(
      `/v2/agent-runs/${runId}/events?after=${cursor}`,
    );
    for (const event of payload.events) {
      cursor = Math.max(cursor, event.id);
      if (consumeEvent(event, onEvent)) return;
    }
    if (["completed", "failed", "cancelled"].includes(payload.run.status)) return;
    await wait(650);
  }
}

async function followWebStream(
  runId: number,
  onEvent: (event: AgentRunEvent) => void,
) {
  let afterId = 0;
  while (true) {
    const response = await fetch(
      `/api/v2/agent-runs/${runId}/stream?after=${afterId}`,
      {
        headers: getAccessToken()
          ? { Authorization: `Bearer ${getAccessToken()}` }
          : {},
      },
    );
    if (!response.ok || !response.body) {
      throw new ApiError("实时连接不可用", response.status || 502);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
      const records = buffer.split(/\r?\n\r?\n/);
      buffer = records.pop() || "";
      for (const record of records) {
        const data = record
          .split(/\r?\n/)
          .filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).trim())
          .join("\n");
        if (!data || data === "{}") continue;
        const event = JSON.parse(data) as AgentRunEvent;
        afterId = Math.max(afterId, event.id);
        if (consumeEvent(event, onEvent)) {
          await reader.cancel().catch(() => undefined);
          return;
        }
      }
      if (done) break;
    }
    const snapshot = await api<{ run: AgentRun }>(`/v2/agent-runs/${runId}`);
    if (["completed", "failed", "cancelled"].includes(snapshot.run.status)) return;
  }
}

export const webAgentTransport: AgentRunTransport = {
  async start(briefId, input) {
    return (
      await api<{ run: AgentRun }>(`/v2/briefs/${briefId}/agent-runs`, {
        method: "POST",
        body: JSON.stringify(input),
      })
    ).run;
  },
  async active(briefId) {
    return (
      await api<{ run: AgentRun | null }>(
        `/v2/briefs/${briefId}/agent-runs/active`,
      )
    ).run;
  },
  async latest(briefId) {
    return api<{ run: AgentRun | null; events: AgentRunEvent[] }>(
      `/v2/briefs/${briefId}/agent-runs/latest`,
    );
  },
  async follow(runId, onEvent) {
    try {
      await followWebStream(runId, onEvent);
    } catch {
      await pollRun(runId, 0, onEvent);
    }
  },
  async cancel(runId) {
    await api(`/v2/agent-runs/${runId}/cancel`, { method: "POST" });
  },
};
